import React from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useBusinessSetupStatus, type SectionKey } from '../../../hooks/useBusinessSetupStatus';
import { useBusinessDetailsQuery } from '../../../hooks/useBusinessDetails';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { deriveBusinessLoadState } from './sections/businessLoadState';
import CreateBusinessCard from './CreateBusinessCard';
import SubmitToDevanzaCard from './SubmitToDevanzaCard';
import BusinessDetailsSection from './sections/BusinessDetailsSection';
import BranchesSection from './sections/BranchesSection';
import ServicesSection from './sections/ServicesSection';
import CategoriesSection from './sections/CategoriesSection';
import ManageStaffSection from './sections/ManageStaffSection';
import CalendarSettingsSection from './sections/CalendarSettingsSection';
// GL-10 (Go-Live Wave 0): PlaceholderSection is no longer rendered here — the
// stub sub-sections (Roles & Commission, Bank Details, Plan & Billing, Tax
// Settings) are hidden for the pilot. The component file is kept for when
// those real sections are wired up.
import styles from './MyBusiness.module.css';

/**
 * AB#1675 — "My Business" onboarding shell.
 *
 * The salon owner lands here from either entry point (sidebar item or
 * profile-menu link). The page is laid out as a two-column shell inside
 * the existing app frame:
 *   - Left: per-section sub-nav grouped into Your Business / Team / Money
 *   - Right: the active sub-section's content
 *
 * Each sub-nav item shows a status dot reflecting whether that section has
 * data yet (empty hollow vs filled green). There is no completion gate —
 * the owner can hop between sections in any order, save when they want,
 * and leave whenever. This replaces the old forced 5-step wizard.
 */

interface SubNavItem {
    key: SectionKey;
    path: string;
    label: string;
    // AB#2066 P1-A — i18n key for the visible label (label kept as the EN fallback).
    labelKey: string;
}

const SECTIONS: { groupLabel: string; groupKey: string; items: SubNavItem[] }[] = [
    {
        groupLabel: 'Your Business',
        groupKey: 'mybiz.group.yourBusiness',
        items: [
            { key: 'businessDetails', path: 'business-details', label: 'Business Details', labelKey: 'mybiz.section.businessDetails' },
            { key: 'branches', path: 'branches', label: 'Branches', labelKey: 'mybiz.section.branches' },
            // CR-2026-05-15 — Categories sits BEFORE Services because a service
            // must belong to a category. The educational hero on the page
            // explains where each category surfaces (marketplace filters,
            // profile badges, marketing reach).
            { key: 'categories', path: 'categories', label: 'Categories', labelKey: 'mybiz.section.categories' },
            { key: 'services', path: 'services', label: 'Services', labelKey: 'mybiz.section.services' },
            // AB#1754 — Calendar Settings (C8 of Calendar v1 Epic AB#1746) replaces
            // the previous "Booking Settings" placeholder. The slot key stays
            // `bookingSettings` so the existing useBusinessSetupStatus wiring
            // continues to resolve; the displayed label + route reflect the new scope.
            { key: 'bookingSettings', path: 'calendar-settings', label: 'Calendar Settings', labelKey: 'mybiz.section.calendarSettings' },
        ],
    },
    {
        groupLabel: 'Team',
        groupKey: 'mybiz.group.team',
        items: [
            { key: 'staff', path: 'staff', label: 'Manage Staff', labelKey: 'mybiz.section.manageStaff' },
            // GL-10 (Go-Live Wave 0): "Roles & Commission" is a PlaceholderSection
            // stub with no backend — hidden from the sub-nav for the pilot.
        ],
    },
    // GL-10 (Go-Live Wave 0): the entire "Money" group (Bank Details, Plan &
    // Billing, Tax Settings) is PlaceholderSection stubs with no backend, so it
    // is hidden from the sub-nav for the pilot. Restore these items (and their
    // routes below) as each real section is wired up.
];

const MyBusiness: React.FC = () => {
    const { sectionsCompleted } = useBusinessSetupStatus();
    const { t } = useLanguage();
    // AB#2108 (Admin V1 P0-2) — an owner with no business gets ONLY the
    // "Create your business" card: the sections' business-scoped calls would 401
    // without the businessId claim (and could race the post-create refresh).
    // Once the business exists, "Submit to Devanza" sits above the sections and
    // shows itself only while the business is a Draft or Rejected.
    const { businessId } = useAuthContext();
    const { data: business, isLoading, isError } = useBusinessDetailsQuery();
    const { noBusinessYet } = deriveBusinessLoadState({ isLoading, isError, business, businessId });

    return (
        <div className={styles.shell}>
            {/* Sub-nav column — section groups with per-section status dots */}
            <aside className={styles.subnav} aria-label={t('mybiz.aria.sections')}>
                {SECTIONS.map(group => (
                    <div key={group.groupLabel} className={styles.subnavGroup}>
                        <h3 className={styles.subnavHeading}>{t(group.groupKey)}</h3>
                        <ul className={styles.subnavList}>
                            {group.items.map(item => {
                                const done = sectionsCompleted[item.key];
                                return (
                                    <li key={item.key}>
                                        <NavLink
                                            to={item.path}
                                            className={({ isActive }) =>
                                                `${styles.subnavLink} ${isActive ? styles.subnavLinkActive : ''}`
                                            }
                                        >
                                            <span
                                                className={`${styles.statusDot} ${done ? styles.statusDotDone : styles.statusDotEmpty}`}
                                                aria-hidden="true"
                                            />
                                            <span>{t(item.labelKey)}</span>
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </aside>

            {/* Content column — the active sub-section */}
            <section className={styles.content}>
                {noBusinessYet ? (
                    <CreateBusinessCard />
                ) : (
                    <>
                        <SubmitToDevanzaCard />
                        <Routes>
                            <Route index element={<Navigate to="business-details" replace />} />
                            <Route path="business-details" element={<BusinessDetailsSection />} />
                            <Route path="branches" element={<BranchesSection />} />
                            <Route path="categories" element={<CategoriesSection />} />
                            <Route path="services" element={<ServicesSection />} />
                            <Route path="calendar-settings" element={<CalendarSettingsSection />} />
                            <Route path="staff" element={<ManageStaffSection />} />
                            {/* GL-10 (Go-Live Wave 0): the placeholder sub-sections
                                (roles, bank-details, plan, tax) are hidden for the
                                pilot. The catch-all redirects any hand-typed URL for a
                                hidden/unknown section back to Business Details so those
                                stub pages are unreachable. */}
                            <Route path="*" element={<Navigate to="business-details" replace />} />
                        </Routes>
                    </>
                )}
            </section>
        </div>
    );
};

export default MyBusiness;
